/**
 * Poll provider-api for an order's TRF signature status (GEN-5027).
 *
 * The order page fetches the signature badge exactly once, on load. That is
 * enough for a status that only ever moves when the page is reloaded, but the
 * embedded-signing flow needs two transitions to land without a manual refresh:
 *
 *   1. **Preparing → sent.** An embedded envelope is created post-commit and
 *      best-effort, so right after submit the order has no signature request yet
 *      (`null`), then a `created`, then a `sent`. The page shows a "preparing
 *      your document for signature" state across that window and needs to notice
 *      when it reaches `sent` so the Sign TRF affordance (GEN-5028) can appear.
 *   2. **sent/delivered → signed.** After the clinician signs, the order should
 *      reflect `signed` without them reloading.
 *
 * This hook drives both by re-reading the order's signature status on an
 * interval and handing the parsed result back to the page. It polls **our own
 * API**, never a signature vendor — DocuSign's no-polling rule targets
 * `Envelopes:get`, not provider-api projecting a status it already stored from
 * Connect.
 *
 * Kept as a standalone hook (not inline in the ~4,600-LOC `OrderInProgress.tsx`)
 * so the page file does not grow another effect and the polling lifecycle is
 * unit-testable on its own.
 */

import { useEffect, useRef, useState } from 'react'

import * as Sentry from '@sentry/react'

import { getOrderTrfSignature } from '@/utils/DatabaseOperations'
import { orderTrfSignatureFrom, type OrderTrfSignature } from '@/utils/orderTrfSignature'

/**
 * How long to wait after each read before re-reading, while the envelope is still
 * being prepared. Measured from when the previous response lands (not a fixed
 * clock), so a slow response spaces out the next read rather than overlapping it.
 */
export const TRF_SIGNATURE_POLL_INTERVAL_MS = 5000

/**
 * Hard cap on how long a single poll session runs. Bounds two cases that would
 * otherwise poll forever: an order at an org with no e-signature (its status
 * stays `null` after submit and no request is ever raised), and a `sent` order
 * left open on screen. Ten minutes is generous enough to catch a clinician who
 * signs in-app and returns, and is the same horizon as the abandonment watcher.
 */
export const TRF_SIGNATURE_POLL_MAX_DURATION_MS = 10 * 60 * 1000

/**
 * How recently an order must have been submitted for a `null` signature status to
 * still count as "preparing". A DocuSign envelope is created within seconds of
 * submit, so a `null` that persists past this window is not an envelope in flight
 * — it is an order that never gets one (predates e-signature, was never placed,
 * or the create failed). Bounding the `null` window on submit recency is what
 * keeps reopening a historical unsigned order from showing the preparing spinner
 * and polling for the full cap. Set to the poll cap so the "preparing" horizon
 * and the poll's own lifetime line up.
 */
export const TRF_SIGNATURE_PREPARING_MAX_AGE_MS = TRF_SIGNATURE_POLL_MAX_DURATION_MS

/**
 * Whether the order was submitted recently enough for a `null` signature to still
 * be an envelope in flight rather than one that will never arrive (see
 * `TRF_SIGNATURE_PREPARING_MAX_AGE_MS`). Uses the order's `updated_at` — submit
 * bumps it, unlike `created_at`, which is stale for a draft placed later — and
 * fails closed (`false`) when it is missing or unparseable, so an order with no
 * usable timestamp is treated as historical rather than perpetually preparing. A
 * future timestamp (client/server clock skew) reads as recent.
 */
export function trfSignatureSubmittedRecently(
  updatedAt: string | Date | null | undefined,
  now: number = Date.now(),
  maxAgeMs: number = TRF_SIGNATURE_PREPARING_MAX_AGE_MS
): boolean {
  if (updatedAt == null) return false
  const ts = updatedAt instanceof Date ? updatedAt.getTime() : Date.parse(updatedAt)
  if (!Number.isFinite(ts)) return false
  return now - ts < maxAgeMs
}

/**
 * Whether the order is still in the post-submit "preparing" window — an embedded
 * envelope is being created but has not reached `sent` yet.
 *
 * `docusignEnabled` covers the gap before any request exists (`null`), where the
 * status alone cannot tell "envelope not created yet" from "org has no
 * e-signature". It is the org's durable DocuSign gate (from `ui_config`), so the
 * preparing window is shown for exactly the orders that will receive an envelope
 * and — unlike a per-visit "just submitted" flag — survives navigating away and
 * back while the envelope is still being prepared. `submittedRecently` bounds that
 * `null` window on submit recency so a historical unsigned order (permanently
 * `null` at a DocuSign org) is not treated as forever-preparing on reopen. Once a
 * request exists, a `created` status is the pre-`sent` state regardless of how the
 * page was reached, so a reload mid-preparation still shows the preparing state.
 */
export function trfSignaturePreparing(
  signature: OrderTrfSignature | null,
  docusignEnabled: boolean,
  submittedRecently: boolean = true
): boolean {
  if (signature == null) return docusignEnabled && submittedRecently
  return signature.status === 'created'
}

/**
 * Whether the envelope is still being prepared and so polling should continue.
 *
 * Polling exists only to carry the order from submit to a request that is out
 * for signature: while the request does not exist yet (`null` right after
 * submit) or is still `created`. The moment it reaches `sent` the request is out
 * for signature and this view has nothing left to refresh, so polling stops rather
 * than keep hitting the API for the rest of the signing lifecycle. `docusignEnabled`
 * gates the `null` window: only an org whose orders receive a DocuSign envelope
 * polls it, so a plain order with no signature request never polls, and — because
 * the gate comes from `ui_config`, not per-visit navigation state — the poll
 * re-arms on its own when the order page is reopened mid-preparation.
 * `submittedRecently` bounds that `null` window on submit recency so reopening a
 * historical unsigned order (permanently `null` at a DocuSign org) does not poll
 * for the full cap.
 *
 * `awaitingReissueFromDocumentId` is one case that polls *past* `sent`: after
 * a material edit provider-api reissues the envelope asynchronously, and the
 * reissue is status-invisible — the order keeps projecting the superseded
 * envelope's `sent` status until the Lambda swaps the document, so only the
 * `externalDocumentId` changes. When set to the superseded document's id, polling
 * continues until the current request is a *different* document (the replacement),
 * or briefly `null` (the old one voided before the new one lands). Recency does
 * not apply here — a reissue is armed explicitly, not inferred from `null`.
 *
 * An embedded-channel envelope is the other case that polls past `sent`: its
 * signer signs inside the app and returns to the order, so the poll must keep
 * running through the signing window (`sent` / `delivered`) to flip the badge to
 * `signed` without a manual reload. The channel is read from each response rather
 * than from org config, because provider-api picks it per order. It stops at any
 * terminal or `signed` status. The per-session time cap bounds an order left
 * unsigned on screen.
 */
export function trfSignatureShouldPoll(
  signature: OrderTrfSignature | null,
  docusignEnabled: boolean,
  awaitingReissueFromDocumentId: string | null = null,
  submittedRecently: boolean = true
): boolean {
  if (awaitingReissueFromDocumentId != null) {
    return signature == null || signature.externalDocumentId === awaitingReissueFromDocumentId
  }
  if (signature == null) return docusignEnabled && submittedRecently
  if (signature.status === 'created') return true
  if (signature.channel === 'embedded' && (signature.status === 'sent' || signature.status === 'delivered')) return true
  return false
}

/** How far along the lifecycle a status is, for detecting a backwards read. */
const STATUS_RANK: Record<OrderTrfSignature['status'], number> = {
  created: 0,
  sent: 1,
  delivered: 2,
  signed: 3,
  declined: 3,
  canceled: 3,
  expired: 3,
}

/**
 * Whether a freshly read status is *behind* the one already on screen for the
 * same request — a stale or out-of-order read that must not be applied.
 *
 * Only a regression *within the same request* is dropped: a different
 * `externalDocumentId` is a reissued envelope (an edit superseded the old one),
 * which legitimately resets the status to `created`/`sent` and must be shown.
 */
export function isTrfSignatureRegression(prev: OrderTrfSignature | null, next: OrderTrfSignature | null): boolean {
  if (prev == null || next == null) return false
  if (prev.externalDocumentId !== next.externalDocumentId) return false
  return STATUS_RANK[next.status] < STATUS_RANK[prev.status]
}

interface UseTrfSignaturePollingParams {
  /** Order whose signature status to poll; polling is off until this is known. */
  orderId: string | null | undefined
  /** provider-api caller string, same one the page's order fetch uses. */
  caller: string
  /** The order's current signature status, as the page currently holds it. */
  signature: OrderTrfSignature | null
  /**
   * Whether the org has DocuSign TRF signing enabled (`ui_config.docusign.enabled`,
   * via `isTrfSignatureDocusignEnabled`). Gates the post-submit `null` window so
   * only orders that will receive an envelope poll, and — being config-derived
   * rather than navigation state — lets the poll resume when the page is reopened.
   */
  docusignEnabled: boolean
  /**
   * Whether the order was submitted recently enough for a `null` signature to
   * still be an envelope in flight (via `trfSignatureSubmittedRecently` over the
   * order's `updated_at`). Bounds the `null` preparing/poll window so a historical
   * unsigned order is not treated as forever-preparing. Defaults to `true` so
   * non-`null` statuses (a real `created`/`sent`) behave the same regardless.
   */
  submittedRecently?: boolean
  /**
   * Set to the superseded envelope's document id after an edit that reissued the
   * TRF, to poll past `sent` until the replacement document lands (see
   * `trfSignatureShouldPoll`). Null (the default) when no reissue is outstanding.
   */
  awaitingReissueFromDocumentId?: string | null
  /** Called with each freshly read status so the page can render it live. */
  onSignature: (signature: OrderTrfSignature | null) => void
  /** Off switch for pages/tests that should never poll (default on). */
  enabled?: boolean
  /** Test seam: override the interval. */
  intervalMs?: number
  /** Test seam: override the max duration. */
  maxDurationMs?: number
}

export interface UseTrfSignaturePollingResult {
  /**
   * Whether to show the "preparing your document for signature" state. This is
   * `trfSignaturePreparing` bounded by the poll's lifetime: once the poll gives
   * up (an order at an org with no e-signature never raises a request, so its
   * status stays `null`), the preparing state clears rather than spinning
   * forever. It resolves the normal way — the moment the envelope reaches `sent`.
   */
  preparing: boolean
}

/**
 * Runs the signature-status poll while the envelope is still being prepared, and
 * reports whether the order is still in its preparing window. Polling ends the
 * moment the status reaches `sent` — the request is then out for signature and
 * this view has nothing more to refresh — except when `awaitingReissueFromDocumentId`
 * is set, where it polls past `sent` until the reissued document replaces the
 * superseded one (the status stays `sent` throughout, only the document changes).
 *
 * The effect re-subscribes only when *whether* to poll changes (see the `active`
 * dependency), not on every status update, so a burst of `created` reads does not
 * tear the poll loop down and rebuild it. The latest status and callback are read
 * through refs for the same reason.
 */
export function useTrfSignaturePolling({
  orderId,
  caller,
  signature,
  docusignEnabled,
  submittedRecently = true,
  awaitingReissueFromDocumentId = null,
  onSignature,
  enabled = true,
  intervalMs = TRF_SIGNATURE_POLL_INTERVAL_MS,
  maxDurationMs = TRF_SIGNATURE_POLL_MAX_DURATION_MS,
}: UseTrfSignaturePollingParams): UseTrfSignaturePollingResult {
  const signatureRef = useRef(signature)
  signatureRef.current = signature
  const onSignatureRef = useRef(onSignature)
  onSignatureRef.current = onSignature

  // Set when a poll session gives up while the order still looks pre-`sent` —
  // either it hit its time cap, or a 404 stopped it (a gone order). Without this,
  // `active` stays true (status is still `created`/`null`) and the preparing cue
  // would keep spinning after the poll has already stopped. Reset whenever a
  // fresh poll session starts (the effect's deps change).
  const [gaveUp, setGaveUp] = useState(false)

  const active =
    enabled &&
    !!orderId &&
    trfSignatureShouldPoll(signature, docusignEnabled, awaitingReissueFromDocumentId, submittedRecently)

  useEffect(() => {
    if (!active || !orderId) return

    // A new poll session: any earlier give-up no longer applies. A no-op when
    // already false, so this does not loop the effect.
    setGaveUp(false)

    let stopped = false
    // Guards against overlapping reads: the scheduled read and the visibility
    // handler can both fire. Only one is ever in flight, so responses apply in the
    // order they were issued and a slow read cannot land after a newer one.
    let inFlight = false
    let timeoutId: ReturnType<typeof setTimeout> | null = null
    const startedAt = Date.now()

    function stop() {
      stopped = true
      if (timeoutId !== null) {
        clearTimeout(timeoutId)
        timeoutId = null
      }
    }

    // Schedule the next read one interval *after the current response*, rather than
    // on a fixed clock: the gap is measured from when we hear back, so a slow
    // response pushes the next read out instead of stacking onto it. Clears any
    // pending timer first so an immediate visibility read resets the cadence to a
    // full interval instead of doubling up.
    const scheduleNext = () => {
      if (timeoutId !== null) clearTimeout(timeoutId)
      if (stopped) return
      timeoutId = setTimeout(() => void poll(), intervalMs)
    }

    const poll = async () => {
      if (stopped || inFlight) return
      if (Date.now() - startedAt >= maxDurationMs) {
        setGaveUp(true)
        stop()
        return
      }
      // A backgrounded tab does not need a fresh status; skip the read but keep the
      // loop alive so it resumes when the tab returns to the foreground.
      if (typeof document !== 'undefined' && document.hidden) {
        scheduleNext()
        return
      }
      inFlight = true
      try {
        const fields = await getOrderTrfSignature(orderId, caller)
        if (stopped) return
        // A 404 (null) is a gone order, not a "no request yet" (which comes back
        // as a 200 with empty fields). Stop without touching the badge, so a
        // transient 404 on an in-flight request cannot clear a resolved status —
        // but mark the session given up so the preparing cue clears rather than
        // spin after the poll has stopped.
        if (fields === null) {
          setGaveUp(true)
          stop()
          return
        }
        const next = orderTrfSignatureFrom(fields)
        // Drop a stale read that would walk the same request backwards (e.g. a
        // slow `created`/`null` landing after the badge already resolved); the
        // initial order fetch writes the same state, so both can race.
        if (!isTrfSignatureRegression(signatureRef.current, next)) {
          onSignatureRef.current(next)
        }
        // Once the read shows there is nothing left to refresh — the envelope has
        // left the preparing window (reached `sent`, or terminal), or, when
        // awaiting a reissue, the replacement document has landed — stop rather
        // than schedule another read.
        if (!trfSignatureShouldPoll(next, docusignEnabled, awaitingReissueFromDocumentId, submittedRecently)) {
          stop()
          return
        }
      } catch (error) {
        // A transient read failure must not kill the poll — we still reschedule
        // below and the next read retries. Report once so a persistently failing
        // poll is still visible.
        Sentry.captureException(error, { tags: { operation: 'poll-trf-signature-status' } })
      } finally {
        inFlight = false
      }
      // Reached only when the poll did not stop: wait one interval from this
      // response before reading again.
      scheduleNext()
    }

    // Kick off the loop: first read one interval after mount.
    scheduleNext()

    // Read once immediately on becoming visible so a tab that was backgrounded
    // during preparation catches the `sent` transition without waiting a full
    // interval when it returns to the foreground.
    const onVisible = () => {
      if (typeof document !== 'undefined' && !document.hidden) void poll()
    }
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', onVisible)
    }

    return () => {
      stop()
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', onVisible)
      }
    }
  }, [
    active,
    orderId,
    caller,
    docusignEnabled,
    submittedRecently,
    awaitingReissueFromDocumentId,
    intervalMs,
    maxDurationMs,
  ])

  // A reissue in flight is also a "preparing" state to show the user: the order
  // is holding a superseded envelope while the replacement is built, and surfacing
  // it is what makes the refresh visible (rather than the badge sitting on a stale
  // `sent`). It ends the same way the poll does — when the new document lands
  // (shouldPoll goes false) or the poll gives up.
  const awaitingReissue =
    awaitingReissueFromDocumentId != null &&
    trfSignatureShouldPoll(signature, docusignEnabled, awaitingReissueFromDocumentId, submittedRecently)

  return {
    preparing: (trfSignaturePreparing(signature, docusignEnabled, submittedRecently) || awaitingReissue) && !gaveUp,
  }
}
